import { Space, Typography } from 'antd';
import EmailNotificationStatusIcon from 'components/ui/composed/email-notification/EmailNotificationStatusIcon';
import { ReactNode } from 'react';
import { Invoice, InvoiceHistoryRecord } from 'store/invoices/types';
import { getCountryLabelByCode } from 'utils/countries';

type ValueType = 'string' | 'number' | 'boolean' | 'date' | 'amount' | 'percents' | 'status';

type FieldConfig = {
    label: string;
    type?: ValueType;
    customFormat?: (v: any) => string;
};

type PartialTaxLine = {
    quantity?: number;
    description?: string;
    unitPrice?: number;
    netAmount?: number;
    taxPercent?: number;
    isFeeLine?: boolean;
};

const FIELD_CONFIG: Record<string, FieldConfig> = {
    dueDate: { label: 'Due Date', type: 'date' },
    issueDate: { label: 'Issue Date', type: 'date' },
    customerName: { label: 'Customer', type: 'string' },
    status: { label: 'Status', type: 'status' },
    invoiceNumber: { label: 'Invoice Number', type: 'string' },
    'feeData.percentage': {
        label: 'Tan Usage Fee',
        type: 'percents',
    },
    'feeData.minAmount': {
        label: 'Minimum Amount',
        type: 'amount',
    },
    'customer.name': { label: 'Customer Name', type: 'string' },
    'customer.address.addressLine1': { label: 'Address', type: 'string' },
    'customer.address.addressLine2': { label: 'Address Line 2', type: 'string' },
    'customer.address.city': { label: 'City', type: 'string' },
    'customer.address.postCode': { label: 'Post Code', type: 'string' },
    'customer.address.country': {
        label: 'Country',
        customFormat: (v: unknown) => (v ? getCountryLabelByCode(String(v)) || String(v) : dash),
    },
};

const dash = '—';

const isLikelyDate = (v: unknown): boolean => typeof v === 'string' && !Number.isFinite(+v) && !isNaN(Date.parse(v));

const formatDate = (v: unknown): string => {
    if (!v) return dash;
    const d = typeof v === 'string' || typeof v === 'number' ? new Date(v) : (v as Date);
    if (Number.isNaN(d.getTime())) return String(v);
    return d.toISOString().slice(0, 10);
};

const formatAmount = (v: unknown): string => {
    if (v == null || v === '') return dash;
    const n = Number(v);
    if (!Number.isFinite(n)) return String(v);
    return n.toFixed(2);
};

const formatPercent = (v: unknown): string => {
    if (v == null || v === '') return dash;
    const n = Number(v);
    if (!Number.isFinite(n)) return String(v);
    return `${n}%`;
};

const formatBoolean = (v: unknown): string => (v === true ? 'Yes' : v === false ? 'No' : dash);

const formatTaxLine = (value: unknown): string => {
    if (!value) return dash;

    if (Array.isArray(value)) {
        return value.map((v) => formatTaxLine(v)).join(', ');
    }

    const v = value as PartialTaxLine;

    const parts: string[] = [];

    if (v.description) {
        parts.push(v.description);
    }

    if (typeof v.taxPercent === 'number') {
        parts.push(`${v.taxPercent}%`);
    }

    if (typeof v.netAmount === 'number') {
        parts.push(`net: ${formatAmount(v.netAmount)}`);
    } else if (typeof v.unitPrice === 'number') {
        parts.push(`unit: ${formatAmount(v.unitPrice)}`);
    }

    if (typeof v.quantity === 'number') {
        parts.push(`qty: ${v.quantity}`);
    }

    return parts.length ? parts.join(' • ') : dash;
};

const asTaxLine = (v: unknown): PartialTaxLine | null => {
    if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
    return v as PartialTaxLine;
};

/**
 * Shows only changed values for a tax line, keeping description as context.
 * Example: "Deferred VAT 0% → 5%"
 */
const formatTaxLineChange = (oldVal: unknown, newVal: unknown): string => {
    const oldLine = asTaxLine(oldVal);
    const newLine = asTaxLine(newVal);

    if (!oldLine || !newLine) {
        return `${formatTaxLine(oldVal)} → ${formatTaxLine(newVal)}`;
    }

    const isFee = oldLine.isFeeLine || newLine.isFeeLine;

    // old.description → new.description
    const oldDesc = oldLine.description || 'Fee';
    const newDesc = newLine.description || 'Fee';

    // If description changed — show only that
    if (isFee && oldDesc !== newDesc) {
        return `${oldDesc} → ${newDesc}`;
    }

    const changes: string[] = [];

    if (oldLine.taxPercent !== newLine.taxPercent) {
        const prev = formatPercent(oldLine.taxPercent);
        const next = formatPercent(newLine.taxPercent);
        changes.push(`${prev} → ${next}`);
    }

    if (!isFee && oldLine.quantity !== newLine.quantity) {
        changes.push(`qty ${oldLine.quantity ?? dash} → ${newLine.quantity ?? dash}`);
    }

    if (!isFee && oldLine.netAmount !== newLine.netAmount) {
        changes.push(`net ${formatAmount(oldLine.netAmount)} → ${formatAmount(newLine.netAmount)}`);
    }

    if (!isFee && oldLine.unitPrice !== newLine.unitPrice) {
        changes.push(`unit ${formatAmount(oldLine.unitPrice)} → ${formatAmount(newLine.unitPrice)}`);
    }

    if (oldLine.isFeeLine !== newLine.isFeeLine) {
        const prev = formatBoolean(oldLine.isFeeLine);
        const next = formatBoolean(newLine.isFeeLine);
        changes.push(`Fee line: ${prev} → ${next}`);
    }

    if (isFee) {
        if (changes.length === 0) {
            return oldDesc;
        }

        if (changes.length === 1) {
            return `${oldDesc} ${changes[0]}`;
        }

        return `${oldDesc}: ${changes.join(', ')}`;
    }

    const desc = newLine.description || oldLine.description || 'Tax line';

    if (changes.length === 0) {
        return desc;
    }

    if (changes.length === 1 && changes[0].includes('%')) {
        return `${desc} ${changes[0]}`;
    }

    return `${desc}: ${changes.join(', ')}`;
};

const defaultFormat = (v: unknown): string => {
    if (v == null || v === '') return dash;
    if (typeof v === 'boolean') return formatBoolean(v);
    if (typeof v === 'number') return String(v);
    if (isLikelyDate(v)) return formatDate(v);
    return String(v);
};

const formatByType = (v: unknown, cfg?: FieldConfig): string => {
    if (cfg?.customFormat) return cfg.customFormat(v);
    switch (cfg?.type) {
        case 'date':
            return formatDate(v);
        case 'percents':
            return formatPercent(v);
        case 'amount': {
            return formatAmount(v);
        }
        case 'boolean':
            return formatBoolean(v);
        case 'number':
            return Number.isFinite(Number(v)) ? String(Number(v)) : String(v);
        case 'status':
        case 'string':
        default:
            return defaultFormat(v);
    }
};

export const getHistoryMetadataBasedOnRecord = (
    record: InvoiceHistoryRecord,
    currencySumbol: string,
    invoice?: Invoice
): {
    title: string;
    description: ReactNode;
} => {
    const { type, fieldName, oldValue, newValue } = record;

    switch (type) {
        case 'SENT': {
            const title = 'Invoice Sent to Customer';
            const emailData = record.email;
            const description = emailData ? (
                <Space size={6} align="start">
                    <div style={{ marginTop: 3 }}>
                        <EmailNotificationStatusIcon
                            overallStatus={emailData.status}
                            providerStatus={emailData.providerEventType}
                        />
                    </div>
                    <Typography.Text>
                        The invoice files have been sent to{' '}
                        <span style={{ color: 'var(--brand-6)' }}>{emailData.recipient}</span>
                    </Typography.Text>
                </Space>
            ) : (
                <Typography.Text>The invoice files have been sent to the customer.</Typography.Text>
            );

            return {
                title,
                description,
            };
        }

        case 'RESENT': {
            const title = 'Invoice Resent to Customer';
            const emailData = record.email;
            const description = emailData ? (
                <Space size={6} align="start">
                    <div style={{ marginTop: 3 }}>
                        <EmailNotificationStatusIcon
                            overallStatus={emailData.status}
                            providerStatus={emailData.providerEventType}
                        />
                    </div>
                    <Typography.Text>
                        The invoice files have been resent to{' '}
                        <span style={{ color: 'var(--brand-6)' }}>{emailData.recipient}</span>
                    </Typography.Text>
                </Space>
            ) : (
                <Typography.Text>The invoice files have been re-sent to the customer.</Typography.Text>
            );

            return {
                title,
                description,
            };
        }

        case 'CREATED':
            return {
                title: 'Invoice Created',
                description: 'Draft invoice',
            };

        case 'PAID':
            return {
                title: 'Invoice Status Updated to “Paid”',
                description: 'The invoice status has been updated.',
            };

        case 'UPDATED': {
            if (fieldName === 'taxLines') {
                return {
                    title: 'Tax Line Updated',
                    description: formatTaxLineChange(oldValue, newValue),
                };
            }

            const cfg = FIELD_CONFIG[fieldName] ?? { label: toTitleCase(fieldName) };
            const oldV = formatByType(oldValue, cfg);
            const newV = formatByType(newValue, cfg);

            const title =
                cfg.label.toLowerCase().includes('date') || cfg.type === 'date'
                    ? `${cfg.label} Updated`
                    : `${cfg.label} Updated`;

            return {
                title,
                description: `${oldV} → ${newV}`,
            };
        }

        case 'CANCELLED': {
            return {
                title: 'Invoice Cancelled',
                description: newValue?.cancellationReason,
            };
        }

        case 'DISPUTED': {
            return {
                title: 'Invoice Disputed',
                description: newValue?.disputeReason,
            };
        }

        case 'PARTIALLY_PAID': {
            const amountPaid = newValue?.amountPaid;
            return {
                title: 'Partial Payment',
                description: `${currencySumbol}${amountPaid}`,
            };
        }

        case 'ARCHIVED':
            return { title: 'Invoice Archived', description: 'The invoice has been archived.' };
        case 'UNARCHIVED':
            return { title: 'Invoice Unarchived', description: 'The invoice has been restored.' };
        case 'EXPORTED_TO_CSV':
            return { title: 'Exported to CSV', description: 'A CSV file was generated.' };
        case 'EXPORTED_TO_PDF':
            return { title: 'Exported to PDF', description: 'A PDF file was generated.' };
        case 'REISSUED':
            return { title: 'Invoice Reissued', description: 'A new version was created.' };

        default:
            return { title: 'Unknown Operation', description: 'No details available.' };
    }
};

const toTitleCase = (s: string) => (s ?? '').replace(/[_.-]+/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());
